import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import clsx from "clsx";
import useLanguageChange from "../../../hooks/useLanguageChange";

import { CALL_BODY_SCREEN, CONNECTING_SCREEN, ENABLED, REDIRECTION } from "../../../constants/Constants";
import { GLOBAL_MEETING_DATA, PublicVariables } from "../../../constants/PublicVariables";
import { SELECTED_SCREEN } from "../../../constants/UploadFilesConstants";
import LanguageDropdown from "./Language/LanguageDropdown";
import navigatorService from "../../../services/navigatorService";
import CallSettings from "./CallSettings/CallSettings";
import BackButton from "./BackButton/BackButton";
import RedirectionTime from "./RedirectionTime/RedirectionTime";
import ChatIconButton from "./ChatButton/ChatIconButton";
import FlipCamera from "./FlipCamera/FlipCamera";
import SessionInfo from "./SessionInfo/SessionInfo";
import { InviteParticipantIconButton } from "./InviteParticipant/InviteParticipantButton";
import { dualStreamEnabled } from "../../../shared/utility";
import RecordingBlinkIcon from "../../Recording/RecordingBlinkIcon";
import { isSilentObserverAvailable, isThirdPartyPlatform } from "../../../shared/function";
import uploadFilesService from "../../../services/uploadFilesService";

const CallTopbar = ({ backButtonClickHandler, changeConnectingScreenStep }) => {
    const { privacyPolicyData } = useSelector((state) => state.onLoadData);
    const { joinMeetingLoading } = useSelector((state) => state.joinMeeting);
    const { isCallDisconnected, isLanguageDropdownBtnVisible, expandView, isRemoteParticipantJoined, isRoomConnected, currentCallBodyScreen } =
        useSelector((state) => state.commonReducer);
    const { isInviteModalOpen } = useSelector((state) => state.inviteParticipant);

    const { currentCameraDevice } = useSelector((state) => state.callSetting);
    const { startLocalVideo } = useSelector((state) => state.twilioEvents);
    const { recordingON } = useSelector((state) => state.recording);
    const { screenToShow } = useSelector((state) => state.uploadFile);

    const [isPlatFormValueAvailable, setIsPlatFormValueAvailable] = useState(false);
    const [timerCounterSecond, setTimerCounterSecond] = useState(REDIRECTION.COUNTER_SECOND);
    const [isCounterTimerVisiable, setIsCounterTimerVisiable] = useState(false);

    const headerBackButtonRef = useRef();

    /* custome hook for getting updated localized value object for entire project 
    This will give localize value object for selected language
    Also will give updated localized value object when language change */
    const localisedValues = useLanguageChange();

    const selectedLanguage = useMemo(() => {
        return PublicVariables.currentLanguageObj;
    }, [localisedValues]);

    useEffect(() => {
        if (isInviteModalOpen || joinMeetingLoading) {
            setIsCounterTimerVisiable(false);
        }
    }, [isInviteModalOpen, joinMeetingLoading]);

    /* Handle to stop and hide timer when click on setting and language dropdown */
    useEffect(() => {
        const settingDropdown = document.getElementById("settings-dropdown");

        settingDropdown?.addEventListener("show.bs.dropdown", function () {
            setIsCounterTimerVisiable(false);
        });

        const languageDropdown = document.getElementById("language-dropdown");
        languageDropdown?.addEventListener("show.bs.dropdown", function () {
            setIsCounterTimerVisiable(false);
        });
    }, [isLanguageDropdownBtnVisible]);

    /* Show back button at top left bar if current screen is Enter guest name screen */
    useEffect(() => {
        const askingGuestName =
            !GLOBAL_MEETING_DATA.isAgent &&
            privacyPolicyData?.sessionConfigurations?.ASKGUESTNAME === ENABLED &&
            changeConnectingScreenStep === CONNECTING_SCREEN.ENTER_GUESTNAME_SCREEN;
        GLOBAL_MEETING_DATA.showBackButton = askingGuestName;
        headerBackButtonRef.current.showHideBackButton(askingGuestName);
    }, [privacyPolicyData, changeConnectingScreenStep]);

    /* Show back button at top left bar if upload file/from camera/from device clicked from main screen */
    useEffect(() => {
        const showBackButton =
            (PublicVariables.featureFlag?.LIVEVIDEO === ENABLED || PublicVariables.featureFlag?.ENABLESCREENSHARE === ENABLED) &&
            screenToShow === SELECTED_SCREEN.SELECT_FILES;
        GLOBAL_MEETING_DATA.showBackButton = showBackButton;
        headerBackButtonRef.current.showHideBackButton(showBackButton);
    }, [screenToShow]);

    /* Show redirection counter for end call and upload image cancle button click */
    useEffect(() => {
        if (isCallDisconnected) {
            isRedirectCounterAvailable();
        }
    }, [isCallDisconnected]);

    const isRedirectCounterAvailable = () => {
        if (GLOBAL_MEETING_DATA.isAgent && isCounterTimerVisiable === false) {
            if (isThirdPartyPlatform()) {
                setIsPlatFormValueAvailable(true);
            }
            if (!navigatorService.isIFrameAvailabe()) {
                // Check whether URL is in IFrame or not
                if (Number(GLOBAL_MEETING_DATA.closetabParams) >= 5 && Number(GLOBAL_MEETING_DATA.closetabParams) <= 120) {
                    setTimerCounterSecond(Math.round(GLOBAL_MEETING_DATA.closetabParams));
                }
                setIsCounterTimerVisiable(true); // Note: Set to true once disconnect API response success
            }
        }
    };

    // For Checking Flip Camera icon is visible or not
    const isFlipCameraAvailable = useMemo(() => {
        return (
            PublicVariables.isMobile &&
            !uploadFilesService.isUploadFilesView() &&
            currentCallBodyScreen !== CALL_BODY_SCREEN.NOTIFY_SCREEN &&
            !isSilentObserverAvailable() &&
            ((dualStreamEnabled() && expandView === false) || !dualStreamEnabled()) &&
            startLocalVideo
        );
    }, [currentCameraDevice, startLocalVideo, expandView, currentCallBodyScreen]);

    return (
        <div className={clsx("call-topbar", screenToShow === SELECTED_SCREEN.FINAL_UPLODING_VIEW && "uploaded-file-call-topbar")}>
            <div className='call-topbar-left'>
                {/* Show Back button with enter guest name screen */}
                <BackButton ref={headerBackButtonRef} clickHandler={backButtonClickHandler} />
                {/* Show Redirection Time counter with reconnect screen  */}
                {isCounterTimerVisiable && (
                    <RedirectionTime
                        closeTabParams={GLOBAL_MEETING_DATA.closetabParams}
                        timerCounterSecond={timerCounterSecond}
                        isPlatFormValueAvailable={isPlatFormValueAvailable}
                        isCounterTimerVisiable={isCounterTimerVisiable}
                        localisedValues={localisedValues}
                    />
                )}
                {isRoomConnected && <SessionInfo />}
            </div>
            <div className='call-topbar-right'>
                <InviteParticipantIconButton />
                <ChatIconButton />
                {isFlipCameraAvailable && <FlipCamera />}
                <CallSettings />
                {/* pending - !$root.uploadFilesView */}
                {isRemoteParticipantJoined && recordingON && <RecordingBlinkIcon />}
                {isLanguageDropdownBtnVisible && <LanguageDropdown selectedLanguage={selectedLanguage} />}
            </div>
        </div>
    );
};

CallTopbar.propTypes = {
    backButtonClickHandler: PropTypes.func,
    changeConnectingScreenStep: PropTypes.string,
};

export default CallTopbar;
